/**
 * Per-step pose validators for the face enrollment flow.
 *
 * Sign conventions (estimatePose output):
 *   yawDeg:   person turns to THEIR right → NEGATIVE (nose moves image-left)
 *             person turns to THEIR left  → POSITIVE
 *   pitchDeg: chin UP (head tilts back)   → NEGATIVE
 *             chin DOWN (head tilts fwd)  → POSITIVE
 *
 * Windows (relaxed 2026-05-26 — wider acceptance, hard cap at ~40° to block
 * extreme/dangerous poses; the original PR #2 windows were too narrow on
 * mobile, where each correction loop costs ~1.5s of inference time and the
 * user could spend 30+ minutes hunting for the sweet spot):
 *
 *   Frontal:    |yaw| ≤ 15° AND |pitch| ≤ 15°
 *   Derecha:    [-40°, -10°]   — clear right turn, no neck strain past 40°
 *   Izquierda:  [+10°, +40°]
 *   Arriba:     [-35°, -8°]    — chin raised, hard cap at 35°
 *   Abajo:      [+8°,  +35°]
 *
 * Frontal-pitch feedback BUG FIX (2026-05-26):
 *   The original code returned `pitch > 0 ? 'Baja...' : 'Sube...'` which is
 *   INVERTED relative to the sign convention above. With pitch < 0 the user's
 *   chin was already raised, but the validator told them to raise more — so
 *   they tilted further up, the next frame reported even more negative pitch,
 *   feedback repeated, and the user got stuck. Real session log showed
 *   pitch=-25, -27, -36 receiving "Sube un poco el mentón" repeatedly.
 *   Corrected: pitch < 0 (chin up) → ask user to LOWER the chin.
 */

export type PoseResult = { ok: boolean; feedback: string };

export const poseValidators: Array<(yaw: number, pitch: number) => PoseResult> = [
  // 0 — Frontal: ±15° on each axis; pitch feedback aligned with sign convention
  (yaw, pitch) => {
    if (Math.abs(yaw) > 15) {
      return { ok: false, feedback: yaw > 0 ? 'Gira ligeramente a la derecha' : 'Gira ligeramente a la izquierda' };
    }
    if (Math.abs(pitch) > 15) {
      // pitch < 0 → chin already UP → tell the user to LOWER it.
      // pitch > 0 → chin already DOWN → tell the user to RAISE it.
      return { ok: false, feedback: pitch < 0 ? 'Baja un poco el mentón' : 'Sube un poco el mentón' };
    }
    return { ok: true, feedback: '' };
  },

  // 1 — Derecha: window [-40°, -10°]
  (yaw, _pitch) => {
    if (yaw > -10) return { ok: false, feedback: 'Gira más a la derecha' };
    if (yaw < -40) return { ok: false, feedback: 'Demasiado giro, vuelve un poco' };
    return { ok: true, feedback: '' };
  },

  // 2 — Izquierda: window [+10°, +40°]
  (yaw, _pitch) => {
    if (yaw < 10) return { ok: false, feedback: 'Gira más a la izquierda' };
    if (yaw > 40) return { ok: false, feedback: 'Demasiado giro, vuelve un poco' };
    return { ok: true, feedback: '' };
  },

  // 3 — Arriba / Sube el mentón: window [-35°, -8°]
  (_yaw, pitch) => {
    if (pitch > -8)  return { ok: false, feedback: 'Sube más el mentón' };
    if (pitch < -35) return { ok: false, feedback: 'Demasiado, baja un poco el mentón' };
    return { ok: true, feedback: '' };
  },

  // 4 — Abajo / Baja el mentón: window [+8°, +35°]
  (_yaw, pitch) => {
    if (pitch < 8)  return { ok: false, feedback: 'Baja más el mentón' };
    if (pitch > 35) return { ok: false, feedback: 'Demasiado, sube un poco el mentón' };
    return { ok: true, feedback: '' };
  },
];
